import _ from 'lodash';
import Element from './Element';
import State from './State';
import * as members from '../privates/privateMembers';
import {validate} from '../privates/validations/validation';
import platformUtils from '../privates/platformUtils';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import ContainableMixin from './mixins/ContainableMixin';
import ClickableMixin from './mixins/ClickableMixin';
import mixin from '../privates/utils/mixin';
import reporter from '../privates/reporter';
import {generateError} from '../privates/validations/errors';
import {setStageUnfriendlyFunctions} from '../privates/utils/livePreview';

const validateStateInfo = (compType, stateInfo, stateIndex, stateboxID) => {
    if (!validate({
        compType,
        functionName: 'changeState',
        dataToValidate: _.isNil(stateInfo) ? {} : {stateInfo},
        shouldCheckMandatoryFields: true,
        compName: stateboxID
    })) {
        return false;
    }
    if (_.isObject(stateInfo) && !(stateInfo instanceof State)) {
        reporter.reportSdkError(generateError.error_type({
            functionName: 'changeState',
            propertyName: 'stateInfo',
            wrongValue: stateInfo,
            expectedType: 'state,string'
        }));
        return false;
    }

    if (stateIndex < 0) {
        if (stateIndex instanceof State) {
            reporter.reportSdkError(generateError.error_state_input({
                propertyName: 'stateInfo',
                functionName: 'changeState',
                stateboxID,
                wrongValue: stateInfo}));

        } else {
            reporter.reportSdkError(generateError.error_state_input({
                propertyName: 'stateInfo',
                functionName: 'changeState',
                stateboxID,
                wrongValue: stateInfo
            }));
        }
        return false;
    }

    return true;
};

const computeStateIndex = (RMI, compId, stateInfo) => {
    const isStateInstance = stateInfo instanceof State;
    const children = RMI.getChildren(compId);

    if (isStateInstance) {
        return children.indexOf(members._compId.get(stateInfo));
    }

    const componentIds = RMI.getCompIdsFromRole(stateInfo);
    return _.findIndex(children, (childId) => _.includes(componentIds, childId));
};

/**
 * @class MultiStateBox
 * @summary Multi-state boxes are containers for states.
 * @description
 * A [multi-state box](https://support.wix.com/en/article/corvid-about-multi-state-boxes) contains
 * multiple states, each with different content. A multi-state box displays one state at a time.
 *
 * For a tutorial on working with your multi-state box and Corvid, click [here](https://support.wix.com/en/article/corvid-working-with-multi-state-boxes-and-code).
 * @mixes $w.Element
 * @mixes $w.HiddenCollapsedMixin
 * @mixes $w.ClickableMixin
 * @mixes $w.ContainableMixin
 * @memberof $w
 * @viewername wysiwyg.viewer.components.StateBox
 * @viewername wysiwyg.viewer.components.StateStrip
 * @definitionId wysiwyg.viewer.components.StateBox
 * @definitionId wysiwyg.viewer.components.StateStrip
 * @tagname multistatebox
 */
class MultiStateBox extends Element {

    /**
     * @function onChange
     * @syntax
     * function onChange(handler: EventHandler): MultiStateBox
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the multi-state box moves to a new state.
     * @description
     *  `onChange` is fired when the multi-state box moves to a new state. This occurs
     *  when the [`changeState()`](#changeState) function is called.
     *
     * @param {$w.EventHandler} eventHandler The name of the function or
     *  the function expression to run when the multi-state box moves to a new state.
     * @returns {$w.MultiStateBox} The multi-state box on which the event is now registered.
     * @snippet [Multistatebox-onChange.es6=Get the ID of the state to which the multi-state box moved]
     * @see [`changeState( )`](#changeState)
     * @memberof $w.MultiStateBox
     * @eventtype change
     * @eventhandler
     */
    onChange(callback) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onChange',
            dataToValidate: {onChange: callback},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().CHANGE, controllerId, callback);
        return this;
    }

    /**
     * @member currentState
     * @label property
     * @syntax
     * get currentState(): State
     * @summary Gets the multi-state box's current state.
     * @snippet [Multistatebox-currentState.es6=Get the current state and its ID]
     * @type {$w.State}
     * @memberof $w.MultiStateBox
     * @instance
     * @readonly
     */
    get currentState() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        const currentStateIndex = RMI.getState(compId).currentIndex;
        const currentStateCompId = RMI.getChildren(compId)[currentStateIndex];
        const currentStateType = RMI.getType(currentStateCompId);

        return wixSelector.getInstance(RMI, currentStateType, currentStateCompId, controllerId);
    }

    /**
     * @function changeState
     * @syntax
     * function changeState(stateReference: String | State): Promise<State>
     * @summary Change the multi-state box's current state to a specific state.
     * @description
     *  The `changeState()` function returns a Promise that is resolved when
     *  the multi-state box finishes moving from the current state to the state
     *  referred to by `stateReference`. `stateReference` can be a state object or
     *  a state [`id`]($w.State.html#id).
     *
     *  You can retrieve [`State`]($w.State.html) objects to pass to the
     *  `stateReference` parameter from your multi-state box using the
     *  [`currentState`](#currentState) or [`states`](#states) properties.
     *
     * @snippet [Multistatebox-changeState-id.es6=Move to a new state using an id]
     * @snippet [Multistatebox-changeState-object.es6=Move to a new state using a [`State`]($w.State.html) object]
     *  This example moves to the state that is stored in the `state` variable.
     * @snippet [Multistatebox-changeState-done.es6=Move to a new state and log a message when the move is finished]
     * @snippet [Multistatebox-changeState-scenario.es6=Switch between 2 states]
     *  This example demonstrates how to switch back and forth between 2 states. The multi-state box has 2 states, with IDs `state1` and `state2`. Each state has a button. When a site visitor clicks the button, the multi-state box moves to the other state.
     * @snippet [Multistatebox-changeState-scenario2.es6=State per Status]
     *  This example demonstrates how to display different product badges for products on a Wix Stores product page. When a site visitor navigates to a product, we check the product's status (out of stock, on sale) and change the state to display the appropriate badge.
     * @param {external:String|$w.State} stateReference The state to move to. Either the [`id`]($w.State.html#id) of the state or a [`State`](#w.State.html) object from the multi-state box.
     * @returns {external:Promise}
     * @fulfill {$w.State} The state that the multi-state box changed to.
     * @reject {external:Error} Error message: If no argument was provided or the argument is not a string or an existing state.
     * @memberof $w.MultiStateBox
     */

    changeState(stateInfo) {
        return new Promise((resolve, reject) => {
            const compType = members._type.get(this).compType;
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const wixSelector = members._wixSelector.get(this);
            const controllerId = members._controllerId.get(this);

            const stateIndex = computeStateIndex(RMI, compId, stateInfo);
            if (!validateStateInfo(compType, stateInfo, stateIndex, compId)) {
                return reject(generateError.error_state_input({
                    propertyName: 'stateInfo',
                    functionName: 'changeState',
                    stateboxID: this.id,
                    wrongValue: stateInfo
                }));
            }

            RMI.executeCompBehavior(compId, 'changeState', {stateIndex}, () => {
                const childrenIds = RMI.getChildren(compId);
                const stateCompID = childrenIds[stateIndex];
                const state = RMI.getComp(stateCompID);
                resolve(wixSelector.getInstance(RMI, state.type, stateCompID, controllerId));
            });
        });
    }

    /**
     * @member states
     * @label property
     * @syntax
     * get states(): State[]
     * @summary Gets a list of all the states contained in the multi-state box.
     * @snippet [Multistatebox-states.es6=Get a multi-state box's states]
     * @type {$w.State[]}
     * @memberof $w.MultiStateBox
     * @instance
     * @readonly
     */
    get states() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const wixSelector = members._wixSelector.get(this);
        const controllerId = members._controllerId.get(this);

        return RMI.getChildren(compId).map((id) => wixSelector.getInstance(RMI, RMI.getComp(id).type, id, controllerId));
    }

    toJSON() {
        const {id, type, global, rendered, currentState} = this;
        return {id, type, global, rendered, currentStateId: currentState.id};
    }
}

mixin(MultiStateBox.prototype, HiddenCollapsedMixin.prototype);
mixin(MultiStateBox.prototype, ClickableMixin.prototype);
mixin(MultiStateBox.prototype, ContainableMixin.prototype);

if (!platformUtils.isExperimentOpen('se_allowChangeStateInLivePreview')) {
    setStageUnfriendlyFunctions(MultiStateBox, ['changeState']);
}

export default MultiStateBox;
